// App Layout - Three-Pane Structure
import { Outlet } from 'react-router-dom';
import { useState, useEffect, createContext, useRef, useMemo } from 'react';
import { Zap, Settings as SettingsIcon, PanelLeft } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import Button from '@/components/Button';
import { ResizablePane } from '@/components/ResizablePane';
import Sidebar from './components/Sidebar';
import { useNavigate } from 'react-router-dom';

export type ViewFilter = 'all' | 'unread' | 'read' | 'starred';

export interface AppLayoutContextType {
  selectedView: ViewFilter;
  setSelectedView: (view: ViewFilter) => void;
  selectedFeedId: number | null;
  setSelectedFeedId: (id: number | null) => void;
  selectedTagId: number | null;
  setSelectedTagId: (id: number | null) => void;
}

// Create React Context for state sharing
export const AppLayoutContext = createContext<AppLayoutContextType | null>(null);

// Keep the interface for Outlet context compatibility
export interface AppLayoutContext extends AppLayoutContextType {}

export default function AppLayout() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Restore feed selection from localStorage
  const [selectedView, setSelectedView] = useState<ViewFilter>(() => {
    const saved = localStorage.getItem('fluxfeed-selected-view');
    return (saved as ViewFilter) || 'all';
  });
  const [selectedFeedId, setSelectedFeedId] = useState<number | null>(() => {
    const saved = localStorage.getItem('fluxfeed-selected-feed-id');
    return saved ? parseInt(saved, 10) : null;
  });
  const [selectedTagId, setSelectedTagId] = useState<number | null>(() => {
    const saved = localStorage.getItem('fluxfeed-selected-tag-id');
    return saved ? parseInt(saved, 10) : null;
  });
  
  // Persist feed selection to localStorage
  useEffect(() => {
    localStorage.setItem('fluxfeed-selected-view', selectedView);
  }, [selectedView]);
  
  useEffect(() => {
    if (selectedFeedId !== null) {
      localStorage.setItem('fluxfeed-selected-feed-id', selectedFeedId.toString());
    } else {
      localStorage.removeItem('fluxfeed-selected-feed-id');
    }
  }, [selectedFeedId]);
  
  useEffect(() => {
    if (selectedTagId !== null) {
      localStorage.setItem('fluxfeed-selected-tag-id', selectedTagId.toString());
    } else {
      localStorage.removeItem('fluxfeed-selected-tag-id');
    }
  }, [selectedTagId]);
  
  // Use refs to track latest values immediately (bypasses React batching)
  // Initialize refs from initial state values
  const selectedFeedIdRef = useRef<number | null>(selectedFeedId);
  const selectedViewRef = useRef<ViewFilter>(selectedView);
  const selectedTagIdRef = useRef<number | null>(selectedTagId);
  
  // Force re-render when refs update (for Context Provider)
  // Use a counter to force context value updates
  const [contextVersion, setContextVersion] = useState(0);
  
  // Keep refs in sync with state (this ensures refs are always up-to-date)
  useEffect(() => {
    selectedFeedIdRef.current = selectedFeedId;
    selectedViewRef.current = selectedView;
    selectedTagIdRef.current = selectedTagId;
    // Force context update when state changes
    setContextVersion(v => v + 1);
  }, [selectedView, selectedFeedId, selectedTagId]);

  // Debug: Log state changes with actual values
  useEffect(() => {
    console.log('AppLayout state changed:', { 
      selectedView, 
      selectedFeedId, 
      selectedTagId,
      selectedFeedIdType: typeof selectedFeedId,
      selectedFeedIdValue: selectedFeedId
    });
    console.log('AppLayout Provider will provide:', { selectedView, selectedFeedId, selectedTagId });
  }, [selectedView, selectedFeedId, selectedTagId]);

  return (
    <div className="flex h-screen flex-col bg-background">
      {/* Top Header */}
      <header className="flex h-14 items-center justify-between border-b border-border px-4">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-primary" />
          <span className="font-semibold">FluxFeed</span>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/app/settings')}
            title="Settings"
          >
            <SettingsIcon className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Main Content Area - Three Panes */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Folders and Feeds */}
        {!sidebarCollapsed && (
          <ResizablePane
            defaultWidth={256}
            minWidth={150}
            maxWidth={500}
            storageKey="fluxfeed-sidebar-width"
            className="border-r border-border bg-muted/30"
          >
            <Sidebar
              collapsed={sidebarCollapsed}
              onToggle={setSidebarCollapsed}
              selectedView={selectedView}
              onSelectView={(view) => {
                console.log('AppLayout onSelectView called with view:', view);
                // Update ref immediately, then state, then force re-render
                selectedViewRef.current = view;
                setSelectedView(view);
                setSelectedFeedId(null); // reset feed filter when switching view
                setSelectedTagId(null); // reset tag filter when switching view
                // Force context update
                setContextVersion(v => v + 1);
              }}
              selectedFeedId={selectedFeedId}
              onSelectFeed={(id) => {
                console.log('AppLayout onSelectFeed called with id:', id, 'current state:', { selectedView, selectedFeedId, selectedTagId });
                // Update ref immediately, then state, then force re-render
                selectedFeedIdRef.current = id;
                // Only reset view to 'all' if actually selecting a feed (not clearing)
                if (id !== null) {
                  selectedViewRef.current = 'all';
                  setSelectedView('all'); // feed view implies all articles from that feed
                }
                selectedTagIdRef.current = null;
                setSelectedFeedId(id);
                setSelectedTagId(null);
                // Force context update immediately
                setContextVersion(v => v + 1);
                console.log('AppLayout state after update - selectedFeedId should be:', id, 'ref value:', selectedFeedIdRef.current);
              }}
              selectedTagId={selectedTagId}
              onSelectTag={(id) => {
                setSelectedTagId(id);
                setSelectedFeedId(null);
                setSelectedView('all');
              }}
            />
          </ResizablePane>
        )}
        {sidebarCollapsed && (
          <div className="flex w-16 flex-col border-r border-border bg-muted/30">
            <div className="flex flex-col gap-2 p-2">
              <Button variant="ghost" size="icon" onClick={() => setSidebarCollapsed(false)} title="Expand sidebar">
                <PanelLeft className="h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Main Content - Outlet for nested routes */}
        <div className="flex-1 overflow-hidden">
          <AppLayoutContext.Provider
            value={useMemo(() => {
              // Create context value - use refs for immediate values, state for persistence
              // This ensures consumers always get the latest value even before state updates
              // useMemo with refs as dependencies ensures the value updates when refs change
              const contextValue: AppLayoutContextType & {
                getSelectedFeedId: () => number | null;
                getSelectedView: () => ViewFilter;
                getSelectedTagId: () => number | null;
              } = {
                // Use refs for immediate access (updated synchronously)
                // State values are used for persistence and re-renders
                selectedView: selectedViewRef.current,
                setSelectedView: (view: ViewFilter) => {
                  selectedViewRef.current = view;
                  setSelectedView(view);
                  setContextVersion(v => v + 1);
                },
                selectedFeedId: selectedFeedIdRef.current,
                setSelectedFeedId: (id: number | null) => {
                  console.log('Context Provider setSelectedFeedId called with:', id, 'current ref:', selectedFeedIdRef.current);
                  selectedFeedIdRef.current = id;
                  setSelectedFeedId(id);
                  setContextVersion(v => v + 1);
                },
                selectedTagId: selectedTagIdRef.current,
                setSelectedTagId: (id: number | null) => {
                  selectedTagIdRef.current = id;
                  setSelectedTagId(id);
                  setContextVersion(v => v + 1);
                },
                // Expose refs for immediate access (same as above, but explicit)
                getSelectedFeedId: () => selectedFeedIdRef.current,
                getSelectedView: () => selectedViewRef.current,
                getSelectedTagId: () => selectedTagIdRef.current,
              };
              
              // Debug: Log context value being provided
              console.log('AppLayoutContext.Provider value:', {
                selectedView: contextValue.selectedView,
                selectedFeedId: contextValue.selectedFeedId,
                selectedTagId: contextValue.selectedTagId,
                stateFeedId: selectedFeedId,
                refFeedId: contextValue.getSelectedFeedId(),
              });
              
              return contextValue;
            }, [contextVersion, selectedView, selectedFeedId, selectedTagId])}
          >
            <Outlet
              context={{
                selectedView,
                setSelectedView,
                selectedFeedId,
                setSelectedFeedId,
                selectedTagId,
                setSelectedTagId,
              }}
            />
          </AppLayoutContext.Provider>
        </div>
      </div>
    </div>
  );
}
